import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MessageInput from './MessageInput';
import SuggestionIcon from '@mui/icons-material/LightbulbOutlined';
// START: Import new icons for features
import AutoStoriesOutlined from '@mui/icons-material/AutoStoriesOutlined';
import InsertChartOutlined from '@mui/icons-material/InsertChartOutlined';
// END: Import new icons for features
import { motion } from 'framer-motion';

interface WelcomeScreenProps {
  onSendMessage: (message: string) => void;
  isGenerating: boolean;
  onCancel: () => void;
  models: {
    [key: string]: { name: string; model: string; description: string };
  };
  selectedModel: string;
  handleModelClick: (event: React.MouseEvent<HTMLElement>) => void;
  databases: {
    [key: string]: { name: string; database: string; type: string };
  };
  selectedDatabase: string;
  handleDatabaseClick: (event: React.MouseEvent<HTMLElement>) => void;
  styles: {
    [key: string]: { name: string; style: string; type: string };
  };
  selectedStyle: string;
  handleStyleClick: (event: React.MouseEvent<HTMLElement>) => void;
  suggestionPrompt: () => Promise<string[]>;
  hideDatabaseSelector?: boolean;
  isWidget?: boolean;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({
  onSendMessage,
  isGenerating,
  onCancel,
  models,
  selectedModel,
  handleModelClick,
  databases,
  selectedDatabase,
  handleDatabaseClick,
  styles,
  selectedStyle,
  handleStyleClick,
  suggestionPrompt,
  hideDatabaseSelector,
  isWidget,
}) => {
  const theme = useTheme();
  const defaultSuggestions = [
    "ระเบียบการแต่งตั้งล่าสุด",
    "คำสั่งแต่งตั้งพนักงาน",
    "ข้อมูลจำนวนนักท่องเที่ยวต่างชาติ ของทั้งปี 2024",
    "เปรียบเทียบนโยบายการท่องเที่ยว",
    "มาตรการจูงใจสร้างท่องเที่ยง"
  ];
  
  const [suggestions, setSuggestions] = useState<string[]>(defaultSuggestions);

  useEffect(() => {
    const fetchSuggestions = async () => {
      const storedSuggestions = sessionStorage.getItem("suggestions");

      if (storedSuggestions) {
        setSuggestions(JSON.parse(storedSuggestions));
      } else {
        try {
          const fetchedSuggestions = await suggestionPrompt();
          const suggestionsToUse = (fetchedSuggestions && fetchedSuggestions.length > 0) 
                                    ? fetchedSuggestions 
                                    : defaultSuggestions;
          setSuggestions(suggestionsToUse);
          sessionStorage.setItem("suggestions", JSON.stringify(suggestionsToUse));
        } catch (err) {
          console.error("Error fetching suggestions:", err);
          setSuggestions(defaultSuggestions);
        }
      }
    };

    fetchSuggestions();
  }, [suggestionPrompt]);

  const chipStyle = {
    flexShrink: 0,
    color: 'white',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.25)',
    whiteSpace: 'nowrap',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        transform: 'translateY(-2px)',
    },
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      height="100lvh"
      position="relative"
      sx={{ overflowX: 'hidden', overflowY: 'auto' }}
    >
      <img
        src="/bg.png"
        alt="Background"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0,
          filter: 'blur(4px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.35)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      <Box
        position="relative"
        zIndex={1}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        flexGrow={1}
        px={4}
        py={2}
      >
        <Box 
          flexGrow={1}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="flex-end"
          width="100%"
          position="relative"
          overflow="hidden"
        >
          <Box
            sx={{
              position: 'relative',
              width: 'clamp(150px, 15vw, 180px)',
              height: 'auto',
              marginBottom: '1.5rem'
            }}
          >
            <motion.img
              src="/avatar.png"
              alt="Avatar"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: [0, -15, 0], opacity: 1 }}
              transition={{
                y: { duration: 3.5, repeat: Infinity, ease: 'easeInOut' },
                opacity: { duration: 0.7 }
              }}
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>

          <Typography
            variant="h4"
            color="white"
            fontWeight="600"
            textAlign="center"
            sx={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}
          >
            TATAI Copilot
          </Typography>
        </Box>

        <Box 
            width="100%"
            maxWidth={{ xs: '100%', md: 960 }}
            mt={4}
            mx="auto"
        >
          <MessageInput
            onSendMessage={onSendMessage}
            onCancel={onCancel}
            isGenerating={isGenerating}
            models={models}
            selectedModel={selectedModel}
            handleModelClick={handleModelClick}
            databases={databases}
            selectedDatabase={selectedDatabase}
            handleDatabaseClick={handleDatabaseClick}
            styles={styles}
            selectedStyle={selectedStyle}
            handleStyleClick={handleStyleClick}
            mode="welcome"
            suggestionPrompt={suggestionPrompt}
            hideDatabaseSelector={hideDatabaseSelector}
          />
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          pb: { xs: 1.5, sm: 2 },
        }}
      >

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
            mb: { xs: 1.5, sm: 2 },
            width: '100%',
          }}
        >
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)'}}>
            ลองใช้ฟีเจอร์ใหม่
          </Typography>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              [theme.breakpoints.down('sm')]: {
                overflowX: 'auto',
                justifyContent: 'flex-start',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
              },
              [theme.breakpoints.up('sm')]: {
                justifyContent: 'center',
              },
            }}
          >
            <Box
              display="flex"
              gap={1.5}
              sx={{
                flexWrap: 'nowrap',
                px: 2,
                [theme.breakpoints.up('sm')]: {
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                },
              }}
            >
              <Chip
                icon={<AutoStoriesOutlined sx={{ color: 'rgba(255,255,255,0.8)' }} />}
                label="รวมข้อมูล DMS, TATIC และ Internet"
                onClick={() => onSendMessage("ข่าว ττท. ล่าสุด")}
                sx={chipStyle}
              />
              <Chip
                icon={<InsertChartOutlined sx={{ color: 'rgba(255,255,255,0.8)' }} />}
                label="สร้าง Infographic"
                onClick={() => onSendMessage("สร้าง infographic พฤติกรรมการท่องเที่ยว จ.อุดรธานี")}
                sx={chipStyle}
              />
            </Box>
          </Box>
        </Box>
        {/* END: New Features Section */}

        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)'}}>
          ไอเดียคำถามสำหรับคุณ
        </Typography>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            [theme.breakpoints.down('sm')]: {
              overflowX: 'auto',
              justifyContent: 'flex-start',
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
            },
            [theme.breakpoints.up('sm')]: {
              justifyContent: 'center',
            },
          }}
        >
          <Box
            display="flex"
            gap={1.5}
            sx={{
              flexWrap: 'nowrap',
              px: 2,
              [theme.breakpoints.up('sm')]: {
                flexWrap: 'wrap',
                justifyContent: 'center',
                maxWidth: 900,
              },
            }}
          >
            {suggestions.slice(0, 5).map((suggestion, index) => (
            <Chip
                key={index}
                icon={<SuggestionIcon sx={{ color: 'rgba(255,255,255,0.8)' }} />}
                label={suggestion}
                onClick={() => onSendMessage(suggestion)}
                sx={chipStyle}
            />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default WelcomeScreen;